import React from 'react'
import { User, Bot, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react'
import clsx from 'clsx'

const MessageBubble = ({ message, taskData }) => {
  const isUser = message.type === 'user'
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-success-500" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-error-500" />
      case 'processing':
        return <Clock className="w-4 h-4 text-warning-500 animate-pulse" />
      case 'pending':
        return <Clock className="w-4 h-4 text-gray-400" />
      default:
        return null
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-success-50 border-success-200'
      case 'failed': return 'bg-error-50 border-error-200'
      case 'processing': return 'bg-warning-50 border-warning-200'
      case 'pending': return 'bg-gray-50 border-gray-200'
      default: return 'bg-gray-50 border-gray-200'
    }
  }

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  }

  return (
    <div className={clsx(
      'flex animate-fade-in',
      isUser ? 'justify-end' : 'justify-start'
    )}>
      <div className={clsx(
        'flex items-start space-x-2 max-w-xs lg:max-w-md',
        isUser ? 'flex-row-reverse space-x-reverse' : 'flex-row'
      )}>
        {/* Avatar */}
        <div className={clsx(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          isUser 
            ? 'bg-primary-500 text-white' 
            : 'bg-gray-200 text-gray-600'
        )}>
          {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
        </div>

        {/* Message Content */}
        <div className={clsx(
          'px-4 py-2 rounded-lg relative',
          isUser
            ? 'bg-primary-500 text-white'
            : message.error
            ? 'bg-error-50 text-error-800 border border-error-200'
            : 'bg-white text-gray-800 border border-gray-200'
        )}>
          <div className="text-sm leading-relaxed">
            {message.content}
          </div>
          
          {/* Timestamp */}
          <div className={clsx(
            'text-xs mt-1',
            isUser ? 'text-primary-100' : 'text-gray-500'
          )}>
            {formatTime(message.timestamp)}
          </div>

          {/* Task Status */}
          {message.taskId && taskData && (
            <div className={clsx(
              'mt-2 p-2 rounded border text-xs',
              getStatusColor(taskData.status)
            )}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1">
                  {getStatusIcon(taskData.status)}
                  <span className="font-medium capitalize">
                    {taskData.status}
                  </span>
                </div>
                <span className="text-gray-500">
                  ID: {taskData.task_id.slice(-8)}
                </span>
              </div>
              
              {taskData.message && (
                <div className="mt-1 text-gray-600">
                  {taskData.message}
                </div>
              )}

              {taskData.error && (
                <div className="mt-1 text-error-600 flex items-start space-x-1">
                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>{taskData.error}</span>
                </div>
              )}

              {taskData.result && (
                <div className="mt-2">
                  {taskData.download_url ? (
                    <div className="flex items-center space-x-2">
                      <a 
                        href={taskData.download_url}
                        download
                        className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download Results
                      </a>
                      <details className="text-xs">
                        <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                          View Results
                        </summary>
                        <div className="mt-1 p-2 bg-white rounded border max-h-32 overflow-y-auto scrollbar-thin">
                          <pre className="whitespace-pre-wrap text-xs">
                            {JSON.stringify(taskData.result, null, 2)}
                          </pre>
                        </div>
                      </details>
                    </div>
                  ) : (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                        View Results
                      </summary>
                      <div className="mt-1 p-2 bg-white rounded border max-h-32 overflow-y-auto scrollbar-thin">
                        <pre className="whitespace-pre-wrap text-xs">
                          {JSON.stringify(taskData.result, null, 2)}
                        </pre>
                      </div>
                    </details>
                  )}
                </div>
              )}

              {taskData.steps && taskData.steps.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs text-gray-600 mb-1">Steps:</div>
                  <div className="space-y-1">
                    {taskData.steps.slice(-3).map((step, index) => (
                      <div key={index} className="flex items-center space-x-1 text-xs">
                        <div className={clsx(
                          'w-1.5 h-1.5 rounded-full',
                          step.status === 'completed' ? 'bg-success-400' :
                          step.status === 'processing' ? 'bg-warning-400 animate-pulse' :
                          step.status === 'failed' ? 'bg-error-400' : 'bg-gray-300'
                        )} />
                        <span className="truncate">{step.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MessageBubble